import React, { useState } from 'react';
import classNames from 'classnames';
import styles from './googleAddress.module.scss';
import { Field } from 'formik';
import Error from '../builder/error';
// Use Google for address autocompletion
import PlacesAutocomplete from 'react-places-autocomplete';
// Utility functions for Google places
import { geocodeByAddress } from 'react-places-autocomplete';

// Custom code, refactored into separate for readability
import { validateGeocodedAddress } from './validateGeocodedAddress';
import { setFormikFieldValues } from './formikInteraction';

function GoogleAddress(props) {
  const [showGoogle] = useState(true);
  const [addressValue, setAddressValue] = useState('');

  function handleAddressChange(address) {
    setAddressValue(address);
    props.setFieldValue(props.name, address);
  }

  // This function is called when the user types something into the
  // (Google-activated) address 1 field but does not select an option
  // and then clicks somewhere else.
  function handleAddressError(error, setFieldValueFunc, address1FieldName) {
    if (error === 'ZERO_RESULTS') {
      // Zero Google results is OK, setting address1 as user has specified it.
      try {
        // See Formik's setValues function
        setFieldValueFunc(address1FieldName, addressValue);
      } catch (err) {
        console.error('Failed to update field', err);
      }
    } else {
      console.log(
        'Something went wrong with Google address autocompletion!',
        error
      );
    }
  }

  function handleAddressSelect(address, setFieldValue) {
    setAddressValue(address);
    geocodeByAddress(address)
      .then(results => {
        // Autocomplete with first search result
        const first_result = results[0].address_components;

        // Function to make sure all required fields are in place
        let validated_result = validateGeocodedAddress(first_result);

        // Assemble address from Google's response
        let address_components = [];

        /* Get address components
         * "Address components" correspond to form fields. They look something
         * like: ["address1", "address2", "City", "State", "Zip"]
         */
        props.children.forEach(c => {
          address_components.push(c.name);
        });

        // Use setFieldValue (from Formik) to imperatively update form
        let address_first_line = setFormikFieldValues(
          setFieldValue,
          address_components,
          validated_result // Argument for parameter googleGeocodeResult
        );
        return address_first_line;
      })
      .then(address_first_line => {
        // Update this component's state to only show first address line in
        // input field, rather than full address as string
        setAddressValue(address_first_line);
      })
      .catch(error => console.log('Error', error));
  }

  return (
    <>
      <div
        style={{
          display: showGoogle ? 'none' : 'block'
        }}
      >
        <Field
          name={props.name}
          className={classNames(styles.googleIntegration, 'form-control mb-2', {
            'is-invalid': props.touched[props.name] && props.errors[props.name]
          })}
        />
      </div>
      <div className={styles.googleIntegration}>
        <PlacesAutocomplete
          value={addressValue}
          onChange={address => {
            handleAddressChange(address);
          }}
          onError={e => handleAddressError(e, props.setFieldValue, props.name)}
          onSelect={data => {
            handleAddressSelect(data, props.setFieldValue, props.name);
          }}
        >
          {({
            getInputProps,
            suggestions,
            getSuggestionItemProps,
            loading
          }) => {
            return (
              <>
                <div className="mb-2">
                  <input
                    style={{
                      display: showGoogle ? 'default' : 'none'
                    }}
                    className={classNames(
                      'form-control',
                      {
                        'is-invalid':
                          props.touched[props.name] && props.errors[props.name]
                      },
                      'location-search-input'
                    )}
                    {...getInputProps({
                      placeholder: 'Street Address (search)'
                    })}
                    value={props.value}
                  />
                  {/* This input interacts with Formik; should be hidden normally
                   * Classes left in place for debugging reasons
                   */}
                  <div className="autocomplete-dropdown-container">
                    {/* loading && <div>Loading...</div> */}
                    {suggestions.map(suggestion => {
                      const className = suggestion.active
                        ? 'suggestion-item--active'
                        : 'suggestion-item';
                      // inline style for demonstration purpose
                      const style = suggestion.active
                        ? {
                            backgroundColor: '#fafafa',
                            cursor: 'pointer'
                          }
                        : {
                            backgroundColor: '#ffffff',
                            cursor: 'pointer'
                          };
                      return (
                        <div
                          key={suggestion.placeId}
                          {...getSuggestionItemProps(suggestion, {
                            className,
                            style,
                          })}
                        >
                          <span>{suggestion.description}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Error container displays errors, if any */}
                </div>
                <Error>{props.errors[props.name]}</Error>
              </>
            );
          }}
        </PlacesAutocomplete>
      </div>
    </>
  );
}

export default GoogleAddress;
